/**
 * Coverage = merchant-selected deliverable-region datasets (provinces / cities / postal
 * codes), validated at submit so merchants only accept orders they can fulfil.
 *
 * Three things live in three places, on purpose:
 *  - this CATALOG (tiny, code-shared by admin + storefront): the dropdown list + which
 *    form fields each coverage turns into cascading selects;
 *  - the merchant's pick (`config.coverage.id`): rides along in the served form config;
 *  - the bulk region DATA: static JSON files served by the backend at
 *    `GET /public/coverage/:id`, fetched lazily only when a coverage is set.
 */
/**
 * The coverages a merchant can pick. Each entry needs its `file` published on the coverage
 * bucket, with a nesting depth equal to `fields.length`. Add a coverage by uploading that
 * dataset and adding a line here.
 *
 * NOTE: only datasets already on the bucket belong here — an entry with nothing behind it puts
 * an option in the merchant's dropdown that 404s when the storefront fetches it.
 */
export const COVERAGE_CATALOG = [
    {
        id: 'GN-AR',
        file: 'coverage-ar-generic2.min.js',
        label: 'Argentina generic (GN-AR)',
        country: 'AR',
        fields: ['province', 'city', 'postal_code'],
    },
    {
        id: 'DP-AR',
        file: 'COVR-DP-AR.min.js',
        label: 'Argentina Dropi (DP-AR)',
        country: 'AR',
        fields: ['province', 'city', 'postal_code'],
    },
    {
        id: 'GN-BR',
        file: 'Brazil-generic.js',
        label: 'Brazil generic (GN-BR)',
        country: 'BR',
        fields: ['province', 'city'],
    },
    {
        id: 'DP-CL',
        file: 'COVR-DP-CL.min.js',
        label: 'Chile Dropi (DP-CL)',
        country: 'CL',
        fields: ['province', 'city'],
    },
    {
        id: 'GN-CO',
        file: 'COVR-GN-CO.min.js',
        label: 'Colombia generic (GN-CO)',
        country: 'CO',
        fields: ['province', 'city'],
    },
    {
        id: 'DP-CO',
        file: 'COVR-DP-CO.min.js',
        label: 'Colombia Dropi (DP-CO)',
        country: 'CO',
        fields: ['province', 'city'],
    },
    {
        id: 'EF-CO',
        file: 'COVR-EF-CO.min.js',
        label: 'Colombia Effi (EF-CO)',
        country: 'CO',
        fields: ['province', 'city'],
    },
    {
        id: 'DP-MX',
        file: 'coverage-dp-mexico.min.js',
        label: 'Mexico Dropi (DP-MX)',
        country: 'MX',
        fields: ['province', 'city', 'postal_code'],
    },
];
/**
 * The countries Tiendanube/Nuvemshop operates in — the options in the admin's "country you
 * sell in" picker. Deliberately NOT derived from COVERAGE_CATALOG: a merchant must be able to
 * select a country we ship no dataset for yet (Chile) and be told so, rather than have it
 * missing from the list as if it were unsupported by the platform.
 */
export const SUPPORTED_STORE_COUNTRIES = ['AR', 'BR', 'CL', 'CO', 'MX'];
/** Look up a coverage by id. Returns undefined for null/empty or unknown ids. */
export function coverageById(id) {
    if (!id)
        return undefined;
    return COVERAGE_CATALOG.find((c) => c.id === id);
}
/**
 * The form fields a configured coverage turns into cascading selects — its chain, in order.
 *
 * 🚨 These fields are STRUCTURALLY required by the coverage, in the same way the submit button is
 * structurally required by a form. Three separate things break when one of them is hidden:
 *
 *  1. **The cascade dead-ends.** Each level's options are the dataset walked down through the
 *     values above it, so a hidden `province` leaves `city` with an empty dropdown. If `city` is
 *     also required the buyer is trapped — a required field they physically cannot fill.
 *  2. **Coverage stops enforcing anything.** `isWithinCoverage` short-circuits to "deliverable" on
 *     the first empty value, deliberately, so optional fields do not block an order. A hidden
 *     field is always empty, so the merchant's whole "only accept orders I can fulfil" setting
 *     silently does nothing.
 *  3. **The order loses its region.** Nothing populates `province` / `city` / `zipcode`, so the
 *     address reaches Nuvemshop with the platform's placeholders in place of a real destination.
 *
 * Empty when no coverage is configured — the fields are then ordinary and the merchant may hide
 * them freely.
 */
export function coverageFieldKeys(coverage) {
    return coverageById(coverage?.id)?.fields ?? [];
}
/**
 * The coverages a given store may pick: a merchant selling in Colombia has no use for the
 * Mexican dataset, and offering it invites orders they cannot fulfil.
 *
 * `country` is the store's own ISO alpha-2 code (`StoreSettings.country`). Anything we can't
 * match — a country we ship no dataset for (Chile today), or a country we couldn't determine
 * at all — yields an EMPTY list on purpose. Showing the wrong country's regions, or the whole
 * catalog as a "safe" fallback, is what let a Colombian merchant pick Mexican coverage; the
 * caller is expected to explain the empty list instead (see `noneForCountry` in the admin).
 *
 * The one exception is `selectedId` — the coverage already saved in the config is always
 * kept, even when it belongs to another country, so an existing pick never silently
 * disappears from the select, which would look like data loss and would re-save the config
 * as "no coverage".
 */
export function coveragesForCountry(country, selectedId) {
    const code = country?.trim().toUpperCase();
    return COVERAGE_CATALOG.filter((c) => (!!code && c.country === code) || (!!selectedId && c.id === selectedId));
}
//# sourceMappingURL=coverage.js.map